import type { Contract } from '../types';

export type SigningReturnResult = 'signed' | 'declined' | 'partially_signed' | 'pending' | 'error';

export const signingReturnResult = (
  contract: Pick<Contract, 'signingStatus' | 'draftStatus' | 'companySigned' | 'personSigned'>,
  returningPersona?: string
): SigningReturnResult => {
  if (contract.signingStatus === 'signed' || contract.draftStatus === 'signed_by_both' ||
    (contract.companySigned && contract.personSigned)) {
    return 'signed';
  }
  if (contract.signingStatus === 'declined' || contract.draftStatus === 'draft_rejected') {
    return 'declined';
  }
  if (contract.signingStatus === 'expired' || contract.signingStatus === 'failed') {
    return 'error';
  }
  // A previous signature from the other party does not confirm this user's
  // action. Keep the return neutral until their signature has been confirmed.
  const returningPartySigned = returningPersona === 'person'
    ? contract.personSigned
    : ['company', 'builder'].includes(returningPersona ?? '') && contract.companySigned;
  if (returningPartySigned && contract.companySigned !== contract.personSigned) {
    return 'partially_signed';
  }
  return 'pending';
};

export const signingProjectReturnPath = (projectId: string, contractId: string, result: SigningReturnResult) => {
  const params = new URLSearchParams({
    tab: 'contract',
    contractId,
    signingResult: result
  });
  return `/projects/${encodeURIComponent(projectId)}?${params.toString()}`;
};

export const signingReturnFeedback = (result: string | null) => {
  switch (result) {
    case 'signed':
      return {
        type: 'success' as const,
        translationKey: 'contracts.signingReturn.feedback.signed'
      };
    case 'declined':
      return {
        type: 'warning' as const,
        translationKey: 'contracts.signingReturn.feedback.declined'
      };
    case 'partially_signed':
      return {
        type: 'success' as const,
        translationKey: 'contracts.signingReturn.feedback.partiallySigned'
      };
    case 'pending':
      return {
        type: 'warning' as const,
        translationKey: 'contracts.signingReturn.feedback.pending'
      };
    case 'error':
      return {
        type: 'error' as const,
        translationKey: 'contracts.signingReturn.feedback.error'
      };
    default:
      return null;
  }
};
